<script setup lang="ts">
  import Button from "@/base-components/Button";
  import { useFetch } from "@/composables/useFetch";
  import { uri } from "@/constants/config";
  import { ref } from "vue";
  import Lucide from "@/base-components/Lucide";
  import RegionDialog from "@/pages/manager/cloud/list/components/RegionDialog.vue";

  const model = defineModel();
  const emits = defineEmits(["network"]);
  const isLoading = ref(false);
  const isEditing = ref(false);

  async function assign(osRegion) {
    isLoading.value = true;
    const { data } = await useFetch(uri.admin.cloud.region.create, {
      body: {
        externalId: osRegion.id,
        displayName: `Region-${osRegion.id}`,
        isActive: true,
      },
    });
    isLoading.value = false;
    osRegion.assigned = data;
  }

  async function switchState(osRegion) {
    if (!osRegion.assigned) return assign(osRegion);
    isLoading.value = true;
    const { externalId, ...assigned } = osRegion.assigned;
    assigned.isActive = !assigned.isActive;
    const { data } = await useFetch(
      `${uri.admin.cloud.region.create}/${osRegion.assigned.id}`,
      {
        body: assigned,
        method: "PATCH",
      }
    );
    osRegion.assigned = data;
    isLoading.value = false;
  }

  function showNetworkPanel() {
    emits("network", model);
  }
</script>

<template>
  <free-style-shimmer
    v-if="isLoading"
    height="300px"
    width="100%"
    class="w-full"
  />
  <div
    v-if="!isLoading"
    class="rounded-lg p-6 space-y-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow relative box"
    :class="!model.assigned ? 'bg-gray-100' : ''"
    dir="ltr"
  >
    <RegionDialog
      v-if="isEditing"
      v-model="model"
      :show="isEditing"
      @close="isEditing = false"
    />

    <!-- Header Section -->
    <div class="flex items-center justify-between border-b pb-2 mb-4">
      <div class="w-full gap-2">
        <h3 v-if="model.assigned" class="text-xl font-medium text-gray-800">
          <Lucide
            v-if="model.assigned"
            icon="Edit"
            class="h-5 cursor-pointer inline"
            @click="isEditing = true"
          />
          {{
            model.assigned
              ? model.assigned.displayName
              : $t("title.notAssigned")
          }}
        </h3>
      </div>
      <div class="w-36 -mt-3">
        <span class="text-sm text-gray-500">
          {{ model.assigned ? model.assigned.name : "N/A" }}
        </span>
        <Button
          :variant="
            model.assigned
              ? model.assigned.isActive
                ? 'primary'
                : 'warning'
              : 'danger'
          "
          size="sm"
          class="w-16"
          @click="switchState(model)"
        >
          {{
            model.assigned
              ? model.assigned.isActive
                ? $t("title.active")
                : $t("title.inactive")
              : $t("title.assign")
          }}
        </Button>
      </div>
    </div>
    <!-- Middle Data Fields -->
    <div class="gap-4">
      <div class="flex flex-row mb-2">
        <div class="w-full">
          <p class="text-gray-500 text-sm">Location</p>
          <p class="font-medium text-gray-800">
            {{ model.features.location || "N/A" }}
          </p>
        </div>
        <div class="w-36">
          <p class="text-gray-500 text-sm">Zone</p>
          <p class="font-medium text-gray-800">{{ model.zone || "N/A" }}</p>
        </div>
      </div>

      <div class="flex flex-row mb-2">
        <div class="w-full">
          <p class="text-gray-500 text-sm">Code</p>
          <p class="font-medium text-gray-800">
            {{ model.features.code || "N/A" }}
          </p>
        </div>
        <div class="w-full">
          <p class="text-gray-500 text-sm">Quality</p>
          <p class="font-medium text-gray-800">
            {{ model.features.Quality || "N/A" }}
          </p>
        </div>
      </div>
      <!-- Bottom Data Fields - Configuration -->
      <div class="mt-2">
        <div class="flex flex-col">
          <p class="text-gray-500 text-sm">Description</p>
          <p class="font-medium text-gray-800">
            {{ model.features.description || "N/A" }}
          </p>
        </div>
      </div>
    </div>
    <div class="gap-4 border-b space-y-3"></div>
    <div v-if="model.assigned">
      <Button
        variant="warning"
        size="sm"
        class="p-2 w-[100%]"
        @click="showNetworkPanel"
      >
        <Lucide icon="Network" class="inline mr-1" />
        {{ $t("button.networks") }}
      </Button>
    </div>
  </div>
</template>

<style scoped></style>
